import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { MaskedLine, FadeUp, Kicker } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1668120089662-42642838cfef?q=80&w=2000&auto=format&fit=crop";
const IMG_SOIL = "https://images.unsplash.com/photo-1690544254749-c832ef027c72?q=80&w=1200&auto=format&fit=crop";
const IMG_TOOL = "https://images.unsplash.com/photo-1598851418241-f52c34b6e4c3?q=80&w=1200&auto=format&fit=crop";
const IMG_GARDEN = "https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=1600&auto=format&fit=crop";

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  { n: "01", title: "Radici", text: "Ogni giardino comincia dall'ascolto del luogo: la luce, il vento di maestrale, la memoria della terra. Progetto solo dopo aver camminato a piedi nudi nel tuo spazio.", img: IMG_SOIL },
  { n: "02", title: "Metodo", text: "Sopralluogo, disegno, realizzazione, cura. Quattro passi, un solo interlocutore. Seguo personalmente ogni cantiere, dalla prima zolla all'ultima potatura.", img: IMG_TOOL },
  { n: "03", title: "Materia", text: "Essenze mediterranee, pietra locale, legno e corten. Scelgo piante che appartengono a questa isola: belle perché giuste, resistenti perché a casa loro.", img: IMG_GARDEN },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [works, setWorks] = useState([]);
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    document.title = "Luca Mannu Giardini — Giardiniere e Paesaggista in Sardegna";
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
    api.get("/works").then((r) => setWorks(r.data.slice(0, 3))).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-forest">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Giardino mediterraneo curato" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-forest/55" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="font-accent text-xs uppercase tracking-[0.3em] text-terra"
            data-testid="hero-kicker"
          >
            Giardiniere · Paesaggista — Sardegna
          </motion.p>
          <h1 className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight text-bone md:text-6xl" data-testid="hero-title">
            <span className="reveal-line">
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.15, ease: EASE }}>
                Coltivo bellezza,
              </motion.span>
            </span>
            <span className="reveal-line">
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.32, ease: EASE }} className="italic text-[#E8DCCB]">
                disegno giardini.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-bone/75"
          >
            Trasformo cortili, terrazzi ed ettari di terra in giardini
            che respirano. Progettazione, realizzazione e cura del verde in tutta la Sardegna.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/contatti" data-testid="hero-quote-cta" className="rounded-full bg-terra px-8 py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
              Richiedi un preventivo
            </Link>
            <Link to="/lavori" data-testid="hero-works-cta" className="flex items-center gap-2 rounded-full border border-bone/40 px-8 py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:border-bone hover:bg-bone hover:text-forest">
              I miei lavori <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 z-10 hidden text-bone/60 md:block"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
            <ArrowDown className="h-5 w-5" strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="manifesto-section">
        <Kicker>Il manifesto</Kicker>
        <h2 className="mt-4 max-w-2xl font-serif text-4xl tracking-tight text-forest md:text-5xl">
          <MaskedLine delay={0.1}>Tre capitoli,</MaskedLine>
          <MaskedLine delay={0.22} className="italic text-moss">una sola filosofia.</MaskedLine>
        </h2>
        <div className="mt-20 flex flex-col gap-24 lg:gap-32">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className={`grid items-center gap-10 md:grid-cols-12 ${i % 2 ? "" : ""}`}>
              <FadeUp className={`md:col-span-5 ${i % 2 ? "md:order-2 md:col-start-8" : ""}`}>
                <div className="arch-frame overflow-hidden bg-sand">
                  <img src={c.img} alt={c.title} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </FadeUp>
              <div className={`md:col-span-6 ${i % 2 ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
                <FadeUp delay={0.1}>
                  <span className="font-serif text-6xl italic text-line md:text-7xl">{c.n}</span>
                  <h3 className="mt-2 font-serif text-3xl tracking-tight text-forest md:text-4xl">{c.title}</h3>
                  <p className="mt-6 max-w-md leading-relaxed text-sage">{c.text}</p>
                </FadeUp>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODOTTI */}
      <section className="bg-sand/60 py-28 lg:py-36" data-testid="home-products-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>Dal vivaio</Kicker>
              <h2 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl">
                <MaskedLine delay={0.1}>Piante e attrezzi scelti</MaskedLine>
                <MaskedLine delay={0.22} className="italic text-moss">da chi li usa ogni giorno.</MaskedLine>
              </h2>
            </div>
            <Link to="/negozio" data-testid="home-shop-link" className="link-underline flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-forest">
              Tutto il negozio <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-12">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* LAVORI */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="home-works-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>Portfolio</Kicker>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl">
              <MaskedLine delay={0.1}>Lavori che</MaskedLine>
              <MaskedLine delay={0.22} className="italic text-moss">mettono radici.</MaskedLine>
            </h2>
          </div>
          <Link to="/lavori" data-testid="home-works-link" className="link-underline flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-forest">
            Tutti i lavori <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-12">
          {works.map((w, i) => (
            <FadeUp key={w.id} delay={i * 0.12} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
              <Link to={`/lavori/${w.id}`} className="group block" data-testid={`home-work-${w.id}`}>
                <div className={`overflow-hidden ${i === 0 ? "rounded-sm" : "arch-frame"} bg-sand`}>
                  <img src={w.image} alt={w.title} loading="lazy" className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "aspect-[16/10]" : "aspect-[4/5]"}`} />
                </div>
                <div className="mt-4 flex items-baseline justify-between">
                  <h3 className="font-serif text-2xl text-forest">{w.title}</h3>
                  <p className="font-accent text-xs uppercase tracking-[0.2em] text-sage">{w.location} · {w.year}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <EditorialMarquee dark />

      {/* BLOG */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="home-blog-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>Dal taccuino</Kicker>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl">
              <MaskedLine delay={0.1}>Note di un giardiniere.</MaskedLine>
            </h2>
          </div>
          <Link to="/blog" data-testid="home-blog-link" className="link-underline flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-forest">
            Tutti gli articoli <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {posts.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.12}>
              <Link to={`/blog/${p.slug}`} className="group block" data-testid={`home-post-${p.slug}`}>
                <div className="overflow-hidden rounded-sm bg-sand">
                  <img src={p.image} alt={p.title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="mt-5 font-accent text-[10px] uppercase tracking-[0.22em] text-terra">{p.category} · {formatDate(p.created_at)}</p>
                <h3 className="mt-2 font-serif text-2xl leading-snug text-forest transition-colors duration-300 group-hover:text-moss">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{p.excerpt}</p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-forest py-28 lg:py-40" data-testid="home-cta-section">
        <img src={IMG_GARDEN} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Kicker>Preventivi gratuiti</Kicker>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight tracking-tight text-bone md:text-6xl">
              Hai un giardino <span className="italic text-terra">da immaginare?</span>
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-bone/70">
              Raccontami il tuo spazio: ti risponderò entro 48 ore con un sopralluogo gratuito e un preventivo su misura.
            </p>
            <Link to="/contatti" data-testid="cta-quote-button" className="mt-10 inline-block rounded-full bg-bone px-10 py-4 font-accent text-sm uppercase tracking-[0.2em] text-forest transition-transform duration-300 hover:scale-105 active:scale-95">
              Parliamone
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
